// CorporateList.tsx
import React, { FC } from "react";
import { useFetchProfileQuery } from "@features/common/commonApi";
import { useSelectCorporateMutation } from "../corporateSelectAPI";
import styles from "./CorporateList.module.scss";
import { useAppDispatch } from "@redux/hooks";
import { notifyUser } from "@redux/slices/CommonSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CorporateSelectRightArrow } from "customIcons";
import InfiniteScroll from "react-infinite-scroll-component";
import CorporateSkeleton from "@features/common/skeleton/corporateSkeleton/CorporateSkeleton";
import {
  handleRedirectByTab,
  getFirstAccessibleRoute,
} from "@utils/redirectUtils";

// ==================== Interface ====================
interface CorporateListProps {
  corporates: any[];
  isLoading: boolean;
  redirect?: string;
  dataLength?: number;
  fetchMoreData?: () => void;
  hasMore?: boolean;
  loading?: boolean;
}

/**
 * CorporateList Component
 *
 * Displays a list of corporates with infinite scroll functionality.
 * Allows users to select a corporate and redirects to the appropriate page.
 */
const CorporateList: FC<CorporateListProps> = ({
  corporates,
  redirect,
  fetchMoreData,
  dataLength = 0,
  hasMore = false,
  loading = false,
}) => {
  // ==================== Hooks ====================
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { refetch: refetchProfile } = useFetchProfileQuery();
  const [selectCorporate, { isLoading: isSelecting }] =
    useSelectCorporateMutation();

  // ==================== Event Handlers ====================
  /**
   * Redirects user to specified URL or navigates to default page
   */

  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");

  /**
   * Handles corporate selection
   * - Calls API to select corporate
   * - Shows success notification
   * - Redirects to appropriate page based on response
   */
  const handleSelectCorporate = async (corporate: any) => {
    try {
      const res = await selectCorporate({
        corporate_id: corporate.id,
      }).unwrap();

      dispatch(
        notifyUser({
          title: "Corporate Selected Successfully",
          message: "",
        }),
      );
      localStorage.setItem("selected_corporate", JSON.stringify(corporate));

      // Force update of profile data before navigation
      const { data } = await refetchProfile();
      const enabled_navigation = data?.data?.enabled_navigation;
      // Redirect to specified redirect URL if present

      if (redirectUrl) {
        window.location.href = decodeURIComponent(redirectUrl);
        return;
      }

      // Otherwise, redirect based on API response

      const redirectTab = res?.data?.redirect_tab;
      if (redirectTab) {
        handleRedirectByTab(redirectTab, navigate);
      } else {
        const firstRoute = getFirstAccessibleRoute(enabled_navigation);
        navigate(firstRoute);
      }
      return;
    } catch (err) {
      console.error("Error selecting corporate:", err);
    }
  };

  return (
    <div className={styles["corporate-list-container"]}>
      <div className={styles["corporate-list-container__header"]}>
        Choose Your Corporate
      </div>

      {loading ? (
        <CorporateSkeleton />
      ) : (
        <div
          className={styles["corporate-list-container__contents"]}
          id="scrollableDiv"
        >
          <InfiniteScroll
            // pass the function directly so InfiniteScroll will call it
            next={fetchMoreData ?? (() => {})}
            hasMore={!!hasMore}
            dataLength={dataLength || 0}
            loader={""}
            // If you're using a specific scrollable div (not window), keep this; otherwise remove it.
            scrollableTarget="scrollableDiv"
          >
            {corporates.map((corporate) => {
              return (
                <div
                  key={corporate.id}
                  className={
                    styles["corporate-list-container__contents__content"]
                  }
                  onClick={() => handleSelectCorporate(corporate)}
                >
                  {/* Left section - Logo & Name */}
                  <div
                    className={
                      styles[
                        "corporate-list-container__contents__content__left"
                      ]
                    }
                  >
                    <img src={corporate.corporate_logo} alt={corporate.name} />
                    <h4>{corporate.name}</h4>
                  </div>

                  {/* Right section - Arrow icon */}
                  <div
                    className={
                      styles[
                        "corporate-list-container__contents__content__right"
                      ]
                    }
                  >
                    <CorporateSelectRightArrow />
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
};

export default CorporateList;
